/** Support Setup: per-title first-review target, default owner and wait window, and the forward address list.
 *  Admins edit; others read. */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { apiErrorText } from '../../lib/apiErrorText'
import { TITLE_LABELS, TITLE_ORDER, type DeskSetting, type StaffMember } from '../../lib/supportDesk'
import { deskOff, useDeskMe } from './SupportQueue'
import { ForwardAddressList } from '../../components/support/ForwardAddressList'

const FIELD = 'bg-ink-900 border border-ink-500 px-2 py-1 text-paper font-mono text-[13px] disabled:opacity-60'

function Row({ s, staff, isAdmin }: { s: DeskSetting; staff: StaffMember[]; isAdmin: boolean }) {
  const qc = useQueryClient()
  const [hours, setHours] = useState(String(s.first_review_hours))
  const [days, setDays] = useState(String(s.wait_close_days))
  const [owner, setOwner] = useState(s.default_owner_user_id == null ? '' : String(s.default_owner_user_id))
  useEffect(() => {
    setHours(String(s.first_review_hours)); setDays(String(s.wait_close_days))
    setOwner(s.default_owner_user_id == null ? '' : String(s.default_owner_user_id))
  }, [s.first_review_hours, s.wait_close_days, s.default_owner_user_id])

  const h = Number(hours), d = Number(days)
  const valid = Number.isInteger(h) && h >= 1 && h <= 168 && Number.isInteger(d) && d >= 1 && d <= 60
  const dirty = h !== s.first_review_hours || d !== s.wait_close_days
    || (owner === '' ? null : Number(owner)) !== s.default_owner_user_id
  const save = useMutation({
    mutationFn: () => api.patch<DeskSetting>(`/api/support/settings/${s.publication}`, {
      first_review_hours: h, wait_close_days: d, default_owner_user_id: owner === '' ? null : Number(owner),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support'] }),
  })
  const known = s.default_owner_user_id == null || staff.some(u => u.id === s.default_owner_user_id)
  const ownerEmail = s.default_owner_user_id == null ? 'No default owner'
    : staff.find(u => u.id === s.default_owner_user_id)?.email ?? `User #${s.default_owner_user_id} (not active)`

  return (
    <tr className="border-b border-ink-600 align-top">
      <td className="py-3 pr-4 text-paper">{TITLE_LABELS[s.publication] ?? s.publication}</td>
      <td className="py-3 pr-4">
        {isAdmin
          ? <input aria-label={`${TITLE_LABELS[s.publication]} first review target in hours`} type="number" min={1} max={168}
              className={`${FIELD} w-20`} value={hours} onChange={e => setHours(e.target.value)} />
          : <span className="font-mono text-paper-dim">{s.first_review_hours} h</span>}
      </td>
      <td className="py-3 pr-4">
        {isAdmin
          ? <select aria-label={`${TITLE_LABELS[s.publication]} default owner`} className={FIELD} value={owner} onChange={e => setOwner(e.target.value)}>
              <option value="">No default owner</option>
              {!known && <option value={String(s.default_owner_user_id)}>{ownerEmail}</option>}
              {staff.map(u => <option key={u.id} value={u.id}>{u.email}</option>)}
            </select>
          : <span className="text-paper-dim">{ownerEmail}</span>}
      </td>
      <td className="py-3 pr-4">
        {isAdmin
          ? <input aria-label={`${TITLE_LABELS[s.publication]} wait window in days`} type="number" min={1} max={60}
              className={`${FIELD} w-20`} value={days} onChange={e => setDays(e.target.value)} />
          : <span className="font-mono text-paper-dim">{s.wait_close_days} d</span>}
      </td>
      {isAdmin && (
        <td className="py-3">
          <button aria-label={`Save ${TITLE_LABELS[s.publication] ?? s.publication}`} className="label border border-ink-500 px-3 py-1 disabled:opacity-40"
            disabled={!dirty || !valid || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          {save.isError && <p role="alert" className="text-red-400 text-xs mt-1">{apiErrorText(save.error)}</p>}
          {save.isSuccess && !dirty && <p className="text-paper-trace text-xs mt-1">Saved.</p>}
          {!valid && <p className="text-amber-glow text-xs mt-1">Hours 1 to 168, days 1 to 60.</p>}
        </td>
      )}
    </tr>
  )
}

export function SupportSetup() {
  const { staff, isAdmin, meLoading } = useDeskMe()
  const settings = useQuery<DeskSetting[]>({
    queryKey: ['support', 'settings'], queryFn: () => api.get<DeskSetting[]>('/api/support/settings'), retry: false,
  })

  if (deskOff(settings.error)) {
    return (
      <div className="p-8 max-w-[640px]">
        <div className="label mb-2">Customer Support</div>
        <p className="font-serif text-2xl text-paper">Customer Support desk is not switched on</p>
      </div>
    )
  }
  const rows = TITLE_ORDER.map(t => settings.data?.find(s => s.publication === t)).filter((s): s is DeskSetting => !!s)

  return (
    <div className="p-8 space-y-8 max-w-[960px]">
      <header className="border-b border-ink-500 pb-6">
        <div className="label mb-1">Members · customer support</div>
        <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">Support setup</h1>
        {!isAdmin && <p className="mt-2 text-sm text-paper-dim">Only administrators can change these. You can see the current values.</p>}
      </header>

      {settings.isError && <p role="alert" className="text-red-400 text-sm">{apiErrorText(settings.error)}</p>}
      {(settings.isLoading || meLoading) && <p className="text-paper-trace text-sm">Loading…</p>}
      {rows.length > 0 && !meLoading && (
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="label text-left align-bottom">
              <th className="pb-2 pr-4 font-normal">Title</th>
              <th className="pb-2 pr-4 font-normal">First review target (hours)</th>
              <th className="pb-2 pr-4 font-normal">
                Default owner
                {/* Spec: R3's cancellation alert emails the owner, or this default when nobody owns it. */}
                <span className="block normal-case tracking-normal font-sans text-[11px] text-paper-trace mt-1 max-w-[220px]">
                  Used from Release 3, for cancellation alerts on conversations nobody owns. Today, assign owners in the queue.
                </span>
              </th>
              <th className="pb-2 pr-4 font-normal">Wait window (days)</th>
              {isAdmin && <th />}
            </tr>
          </thead>
          <tbody>{rows.map(s => <Row key={s.publication} s={s} staff={staff} isAdmin={isAdmin} />)}</tbody>
        </table>
        </div>
      )}
      <p className="text-xs text-paper-trace max-w-[640px]">
        A conversation not yet reviewed after the target counts as over target. A conversation waiting on the customer
        for longer than the wait window is closed automatically each night, and reopens if they reply.
      </p>

      {!meLoading && <ForwardAddressList isAdmin={isAdmin} />}

      <nav aria-label="Related" className="flex flex-wrap gap-6 label-amber">
        <Link to="/support/saved-replies" className="hover:underline">Saved replies</Link>
        <Link to="/known-issues" className="hover:underline">Known issues</Link>
        <Link to="/unthreaded" className="hover:underline">Unthreaded replies</Link>
        <Link to="/email" className="hover:underline">Email inbox</Link>
      </nav>
    </div>
  )
}
